import React from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import { getPlanLimits } from '../../Config/plans';

interface DowngradeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPlan: 'free' | 'standard' | 'entreprise';
  targetPlan: 'free' | 'standard' | 'entreprise';
  effectiveDate: string;
  isLoading?: boolean;
}

/**
 * Modal A - Demande de downgrade générique
 *
 * Affiché lors de toute demande de downgrade (Entreprise→Standard, Standard→Free, etc.)
 * Montre la comparaison entre le plan actuel et le plan cible.
 *
 * Spec: Message UX A
 */
const DowngradeConfirmationModal: React.FC<DowngradeConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentPlan,
  targetPlan,
  effectiveDate,
  isLoading = false
}) => {
  if (!isOpen) return null;

  const currentLimits = getPlanLimits(currentPlan);
  const targetLimits = getPlanLimits(targetPlan);

  const formatLimit = (limit: number): string => {
    if (limit === -1) return 'Illimité';
    return limit.toString();
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg relative">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-full">
                <FiInfo className="text-blue-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Changer d'abonnement vers {targetPlan.toUpperCase()}
              </h3>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Texte principal */}
          <p className="text-gray-700 mb-4">
            Votre changement d'abonnement prendra effet le{' '}
            <strong>{formatDate(effectiveDate)}</strong>.
          </p>
          <p className="text-gray-600 text-sm mb-6">
            Jusqu'à cette date, vous conservez les fonctionnalités et quotas de votre plan{' '}
            <strong>{currentPlan.toUpperCase()}</strong>.
          </p>

          {/* Bloc "Ce qui va changer" */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Ce qui va changer</p>
            <div className="space-y-2.5">
              {/* Projets */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Projets autorisés</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {formatLimit(currentLimits.projects)}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatLimit(targetLimits.projects)}
                  </span>
                </div>
              </div>

              {/* Architectures */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Architectures autorisées</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {formatLimit(currentLimits.architectures)}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatLimit(targetLimits.architectures)}
                  </span>
                </div>
              </div>

              {/* Déploiements */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Déploiements / mois</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {formatLimit(currentLimits.deployments_per_month)}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatLimit(targetLimits.deployments_per_month)}
                  </span>
                </div>
              </div>

              {/* Fonctionnalités */}
              <div className="pt-2 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <strong>Fonctionnalités :</strong> certaines fonctionnalités seront désactivées selon le plan{' '}
                  {targetPlan.toUpperCase()}.
                </p>
              </div>
            </div>
          </div>

          {/* Note importante */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
            <p className="text-xs text-blue-800">
              <strong>Note :</strong> Aucune donnée ne sera supprimée automatiquement.
            </p>
          </div>

          {/* Boutons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50"
            >
              {isLoading ? 'En cours...' : 'Confirmer le changement'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DowngradeConfirmationModal;
